import * as React from "react";
import { ReactNode } from "react";
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Button,
} from "@fluentui/react-components";
import { ArrowSyncRegular } from "@fluentui/react-icons";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ValidationErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Validation Error Boundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{ 
          padding: "24px", 
          display: "flex", 
          flexDirection: "column", 
          gap: "16px",
          backgroundColor: "#FFFFFF",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <MessageBar intent="error" style={{ maxWidth: "400px", width: "100%" }}>
            <MessageBarBody>
              <MessageBarTitle>Validation Error</MessageBarTitle>
              <div style={{ marginTop: "8px" }}>
                Something went wrong while validating your access. Please try again or contact support.
              </div>
              {this.state.error && (
                <div style={{ 
                  marginTop: "8px", 
                  fontSize: "12px", 
                  color: "#666",
                  fontFamily: "monospace"
                }}>
                  {this.state.error.message}
                </div>
              )}
            </MessageBarBody>
          </MessageBar>
          
          {this.props.onRetry && (
            <Button
              appearance="primary"
              icon={<ArrowSyncRegular />}
              onClick={this.handleRetry}
            >
              Try Again
            </Button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ValidationErrorBoundary;